import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { SignInProvider } from 'fym-shared/src/music24/signInMethods';
import { useTRPCClientAPI } from '../../../../../trpc/trpc';
import { sha256Hex } from '../../../../smartLinks/smlAuth/sso/nonce';

// The server keeps the link state and the nonce for 10 minutes. We stop using them after 8.
export const PREPARED_LINK_MAX_AGE_MS = 8 * 60 * 1000;
// After a failed fetch, wait this long before the next try. We do not poll.
const RETRY_DELAY_MS = 30 * 1000;

export interface PreparedLink {
  // The OAuth state of this attempt. The server bound it to this user and provider.
  state: string;
  // The raw nonce. The server gets this value. Keep it in memory only.
  rawNonce: string;
  // The hex SHA-256 of the raw nonce. Apple gets this value.
  hashedNonce: string;
  fetchedAt: number;
}

export interface UsePreparedLinkReturn {
  ready: boolean;
  failed: boolean;
  // Returns the prepared attempt and marks it as used. It is synchronous, so the provider popup
  // still opens inside the click handler.
  take: () => PreparedLink | null;
  // Fetches a new attempt. Call it after every attempt.
  refresh: () => void;
}

/**
 * Prefetches one connect attempt (`signInMethods.startLink`) for a provider. The browser blocks a
 * popup that does not open inside the click, so the state and the nonce are ready before it.
 * The same shape as `useAppleNonce` of the sign-in page.
 */
export const usePreparedLink = (provider: SignInProvider, enabled: boolean): UsePreparedLinkReturn => {
  const trpcClient = useTRPCClientAPI();
  const trpcClientRef = useRef(trpcClient);
  trpcClientRef.current = trpcClient;
  const [prepared, setPrepared] = useState<PreparedLink | null>(null);
  const [failed, setFailed] = useState(false);
  const preparedRef = useRef<PreparedLink | null>(null);
  const requestIdRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const mountedRef = useRef(true);

  const store = useCallback((value: PreparedLink | null) => {
    preparedRef.current = value;
    if (mountedRef.current) {
      setPrepared(value);
    }
  }, []);

  const refresh = useCallback(() => {
    if (!enabled) {
      return;
    }
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    clearTimeout(timerRef.current);
    store(null);

    const load = async (): Promise<void> => {
      try {
        const { state, nonce } = await trpcClientRef.current.signInMethods.startLink.mutate({ provider });
        const hashedNonce = await sha256Hex(nonce);
        if (!mountedRef.current || requestId !== requestIdRef.current) {
          return;
        }
        store({ state, rawNonce: nonce, hashedNonce, fetchedAt: Date.now() });
        setFailed(false);
        timerRef.current = setTimeout(refresh, PREPARED_LINK_MAX_AGE_MS);
      } catch {
        // Do not log the error. It can hold request data.
        if (mountedRef.current && requestId === requestIdRef.current) {
          setFailed(true);
          timerRef.current = setTimeout(refresh, RETRY_DELAY_MS);
        }
      }
    };
    load().catch(console.error);
  }, [enabled, provider, store]);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    return () => {
      mountedRef.current = false;
      clearTimeout(timerRef.current);
    };
  }, [refresh]);

  const take = useCallback((): PreparedLink | null => {
    const { current } = preparedRef;
    if (!current) {
      return null;
    }
    if (Date.now() - current.fetchedAt > PREPARED_LINK_MAX_AGE_MS) {
      refresh();
      return null;
    }
    clearTimeout(timerRef.current);
    store(null);
    return current;
  }, [refresh, store]);

  const ready = prepared !== null;
  return useMemo(() => ({ ready, failed, take, refresh }), [ready, failed, take, refresh]);
};
